import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import * as Yup from 'yup';
import Moment from 'moment';
import {
  AppCard,
  AppDatePicker,
  AppDeleteModal,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import Translator from '../../../layout/components/helpers/Translator';
import RiskManagerSidebar from '../../../layout/screenSpecifics/Modules/InecoRiskManager/Sidebar/InecoRiskManagerSidebar';
import AppLink from '../../../routing/helpers/AppLink';
import ModuleInecoRiskManagerBase from './ModuleInecoRiskManagerBase';

export default function ModuleInecoRiskManagerRiskHomeScreen() {
  const { moduleBridgeHex, riskID } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const [loading, setLoading] = useState();
  const [risk, setRisk] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [probabilityOptions, setProbabiltyOptions] = useState([]);
  const validationSchema = Yup.object().shape({
    type: Yup.number().nullable().required('Requerido'),
    event: Yup.string().required('Requerido'),
    strategicGoal: Yup.number().nullable().required('Requerido'),
    date: Yup.string().required('Requerido'),
    probability: Yup.number().nullable().required('Requerido'),
    impact: Yup.number().nullable().required('Requerido'),
    state: Yup.number().nullable().required('Requerido'),
  });

  useEffect(() => {
    setProbabiltyOptions(
      moduleData?.inputs?.probabilities?.map((p) => ({
        ...p,
        label: `${p?.label} ${p?.percentage}`,
      })),
    );
  }, []);

  // > LOAD RISK
  const loadRisk = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_inecoRiskManager_risk_get'), {
          moduleBridgeHex,
          riskID,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setRisk(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, riskID],
  );

  useEffect(() => {
    if (riskID) {
      setLoading(true);
      loadRisk();
    }
  }, []);
  // < LOAD RISK

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            riskID
              ? 'api_r_user_module_inecoRiskManager_risk_edit'
              : 'api_r_user_module_inecoRiskManager_risk_add',
          ),
          {
            moduleBridgeHex,
            riskID,
            ...values,
          },
        )
        .then(() => {
          if (isMounted.current) {
            navigate(
              path({
                name: 'user_module_inecoRiskManager_risk_list',
                params: { moduleBridgeHex, page: 1, filtersHex: 0 },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskID],
  );
  // < SAVE

  // > DELETE
  const handleDelete = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_inecoRiskManager_risk_delete'), {
          moduleBridgeHex,
          riskID,
        })
        .then(() => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: 'Completado',
              message: 'Riesgo desactivado con éxito',
              type: 'success',
            });
            navigate(
              path({
                name: 'user_module_inecoRiskManager_risk_list',
                params: { moduleBridgeHex, page: 1, filtersHex: 0 },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, riskID],
  );

  useEffect(() => {
    if (riskID) {
      setLoading(true);
      loadRisk();
    }
  }, [riskID]);
  // < DELETE

  return (
    <>
      <ModuleInecoRiskManagerBase />
      <RiskManagerSidebar
        activeRoute={
          riskID
            ? 'user_module_inecoRiskManager_risk_home'
            : 'user_module_inecoRiskManager_risk_add'
        }
      />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData?.moduleName}`,
                  route: {
                    name: 'user_module_inecoRiskManager_risk_list',
                    params: {
                      moduleBridgeHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: riskID
                    ? 'Editar riesgo u oportunidad'
                    : 'Nuevo riesgo u oportunidad',
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              initialValues={{
                type: risk?.type || null,
                cause: risk?.cause || '',
                event: risk?.event || '',
                effect: risk?.effect || '',
                additionalOwners: risk?.additionalOwners || '',
                owners: risk?.owners || [],
                anotherOwners: risk?.anotherOwners || [],
                agent: risk?.agent || null,
                anotherAgents: risk?.anotherAgents || [],
                organizationProcesses: risk?.organizationProcesses || [],
                date: risk?.date || Moment().format('DD/MM/YYYY'),
                strategicGoal: risk?.strategicGoal || null,
                actionPlan: risk?.actionPlan || null,
                actionPlanText: risk?.actionPlanText || '',
                indicator: risk?.indicator || '',
                probability: risk?.probability || null,
                impact: risk?.impact || null,
                state: risk?.state || null,
              }}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <AppCard
                    header={
                      <div>
                        Identificación
                        {riskID && permissionLevel === 0 && (
                          <>
                            <button
                              type="button"
                              className="btn btn-xs btn-danger float-right"
                              title="Desactivar riesgo u oportunidad"
                              onClick={() => setShowDeleteModal(true)}
                            >
                              <AppRemixIcon icon="delete-bin" />
                            </button>
                            <AppDeleteModal
                              show={showDeleteModal}
                              title="Desactivar riesgo u oportunidad"
                              onClose={() => setShowDeleteModal(false)}
                              onConfirm={handleDelete}
                            />
                          </>
                        )}
                      </div>
                    }
                  >
                    <div className="containfer-fluid">
                      <div className="row">
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={moduleData?.inputs?.types}
                            title="Tipo"
                            value={values?.type}
                            error={errors?.type}
                            onChange={(opt) => {
                              setFieldValue('type', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Causa (debido a)"
                            onChange={(value) => setFieldValue('cause', value)}
                            value={values.cause}
                            error={errors.cause}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Evento (se puede producir)"
                            onChange={(value) => setFieldValue('event', value)}
                            value={values.event}
                            error={errors.event}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Efecto (por lo que)"
                            onChange={(value) => setFieldValue('effect', value)}
                            value={values.effect}
                            error={errors.effect}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppMultiSelect
                            options={moduleData?.inputs?.owners}
                            title="Propietario (owner)"
                            value={values?.owners}
                            onChange={(opt) => {
                              setFieldValue('owners', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            allowSelectAll
                            hasSearchBox
                            hasSelectOptions
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Otros Propietarios"
                            onChange={(value) =>
                              setFieldValue('additionalOwners', value)
                            }
                            value={values.additionalOwners}
                            error={errors.additionalOwners}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={moduleData?.inputs?.strategicGoals}
                            title="Objetivo estratégico"
                            value={values?.strategicGoal}
                            error={errors?.strategicGoal}
                            onChange={(opt) => {
                              setFieldValue('strategicGoal', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Plan de Actuación"
                            onChange={(value) =>
                              setFieldValue('actionPlanText', value)
                            }
                            value={values.actionPlanText}
                            error={errors.actionPlanText}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppMultiSelect
                            options={moduleData?.inputs?.organizationProcesses}
                            title="Procesos de la organización"
                            value={values?.organizationProcesses}
                            onChange={(opt) => {
                              setFieldValue('organizationProcesses', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            allowSelectAll
                            hasSearchBox
                            hasSelectOptions
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppTextArea
                            title="Indicador"
                            onChange={(value) =>
                              setFieldValue('indicator', value)
                            }
                            value={values.indicator}
                            error={errors.indicator}
                            isDisabled={permissionLevel !== 0}
                            resize
                            isClearable
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppDatePicker
                            title="Fecha"
                            value={values?.date}
                            placeholder="DD/MM/YYYY"
                            onChange={(opt) => setFieldValue('date', opt)}
                            isDisabled={permissionLevel !== 0}
                            isClearable
                            showYearDropdown
                            showMonthDropdown
                            hasToggleType
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </AppCard>
                  <AppCard header="Evaluación" className="mt-2">
                    <div className="containfer-fluid">
                      <div className="row">
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={probabilityOptions}
                            title="Probabilidad"
                            value={values?.probability}
                            error={errors?.probability}
                            onChange={(opt) => {
                              setFieldValue('probability', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={moduleData?.inputs?.impacts}
                            title="Impacto"
                            value={values?.impact}
                            error={errors?.impact}
                            onChange={(opt) => {
                              setFieldValue('impact', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                        <div className="col-lg-6">
                          <AppSingleSelect
                            options={moduleData?.inputs?.states.filter((s) =>
                              [7, 8].includes(s?.value),
                            )}
                            title="Estado"
                            value={values?.state}
                            error={errors?.state}
                            onChange={(opt) => {
                              setFieldValue('state', opt);
                            }}
                            isDisabled={permissionLevel !== 0}
                            hasSearchBox
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </AppCard>
                  <div className="row mt-3">
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-sm btn-success float-right"
                        disabled={isSubmitting}
                      >
                        {riskID ? 'Editar' : 'Crear'}
                      </button>
                      <AppLink
                        path="user_module_inecoRiskManager_risk_list"
                        params={{
                          moduleBridgeHex,
                          page: 0,
                          filtersHex: 0,
                        }}
                        title="Cancelar"
                        className="btn btn-sm btn-outline-danger float-right mr-2"
                      >
                        Cancelar
                      </AppLink>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          )}
        </div>
      </main>
    </>
  );
}
